import { AppActionSection } from "Components/AppActionSection";
import { Color } from "Components/Form/Button";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "./styled";
import { useOperationsAppSettingsActions } from "./actions";
import { Skeleton } from "@mui/material";
import { CompanyConfig, CompanyMandatoryFieldsNames } from "Models/company.config";
import { useImmer } from "use-immer";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { ConfigSwitch } from "./OperationsAppSettingsPageConfigSwitch";
import { InventoryTable } from "./OperationsAppSettingsPageInventoryTable";
import { OperationsAppLockScreenConfigDetails } from "./OperationsAppLockScreenConfigDetails";
import { ItemCategoryConfigDetails } from "./OperationsAppSettingsPageItemCategoryConfigDetails";
import { OperationsAppSettingsPagePropertyDamageReportConfig } from "./OperationsAppSettingsPagePropertyDamageReportConfig";
import { useAppState } from "Context/StateContext";
import { UserTypes } from "Util/enums";
import {
  buildOperationsOrdersReportsSharingCatalog,
  getDefaultReportsSharingConfig,
  ReportsSharingCatalogItem,
  ReportsSharingConfigDetails,
  toggleReportsSharingSelectedItem,
} from "./OperationsAppSettingsPageReportsSharing";

const normalizeCompanyConfig = (config: CompanyConfig) => {
  const next = JSON.parse(JSON.stringify(config)) as any;

  if (!next.settings) next.settings = {};
  if (!next.settings.opsAppConfiguration) next.settings.opsAppConfiguration = {};

  if (!next.settings.opsAppConfiguration.propertyDamageReportConfig) {
    next.settings.opsAppConfiguration.propertyDamageReportConfig = {
      propertyDamageReportRequired: false,
      upliftCompletionPhotoRequired: false,
      requireCustomerInformationFields: false,
    };
  }
  if (!next.settings.opsAppConfiguration.quickOrder) {
    next.settings.opsAppConfiguration.quickOrder = {
      enabled: false,
      managementOrderReportEnabled: false,
    };
  }
  if (!next.settings.opsAppConfiguration.showLastFinishedTasks) {
    next.settings.opsAppConfiguration.showLastFinishedTasks = {
      enabled: true,
    };
  }
  if (!next.settings.opsAppConfiguration.reportsSharingConfig) {
    next.settings.opsAppConfiguration.reportsSharingConfig = getDefaultReportsSharingConfig();
  }
  if (!Array.isArray(next.settings.opsAppConfiguration.reportsSharingConfig.selectedItems)) {
    next.settings.opsAppConfiguration.reportsSharingConfig = getDefaultReportsSharingConfig();
  }

  return next as CompanyConfig;
};

export const OperationsAppSettingsPage = () => {
  const { t: translate } = useTranslation();
  const { getAllCheckList, getAllReportGroup, getCompanyConfigs, saveCompanyConfigs } =
    useOperationsAppSettingsActions();
  const { currentUser } = useAppState();
  const { data, isFetching } = getCompanyConfigs();
  const { data: reportGroups, isFetching: isFetchingReportGroups } = getAllReportGroup();
  const { data: checklistData, isFetching: isFetchingChecklists } = getAllCheckList();
  const { mutate, isLoading: isSaving } = saveCompanyConfigs({
    handleSuccess() {
      NotificationToast.success(translate("updatedSuccessfully"));
    },
  });

  const [companyConfig, setCompanyConfig] = useImmer<CompanyConfig | undefined>(undefined);
  const reportsSharingCatalog = useMemo(
    () => buildOperationsOrdersReportsSharingCatalog(reportGroups, checklistData?.data),
    [checklistData?.data, reportGroups]
  );

  useEffect(() => {
    if (data) {
      setCompanyConfig(normalizeCompanyConfig(data));
    }
  }, [data]);

  const handleInventoryFieldChange = (fieldKey: string, property: string, value: boolean) => {
    setCompanyConfig((draft) => {
      if (!draft) return;
      const fieldConfig =
        draft.settings.opsAppConfiguration.fieldsRelatedSettings.inventoryFieldsConfig;
      if (fieldKey in fieldConfig) {
        (fieldConfig as any)[fieldKey][property] = value;
      }
    });
  };

  const handleCategoryToggle = (categoryKey: any, value: boolean) => {
    setCompanyConfig((draft) => {
      if (!draft) return;

      draft.settings.opsAppConfiguration.itemCategoryConfig.mandatoryFieldsActive[
        categoryKey as CompanyMandatoryFieldsNames
      ] = value;
    });
  };

  const handleMandatoryFieldToggle = (categoryKey: any, fieldKey: any, value: boolean) => {
    setCompanyConfig((draft) => {
      if (!draft) return;
      const mf = draft.settings.opsAppConfiguration.itemCategoryConfig.mandatoryFields as Record<
        string,
        Record<string, boolean> | undefined
      >;
      if (!mf[categoryKey] && categoryKey === "TV") {
        mf.TV = {
          ScreenSize: false,
          ScreenType: false,
          RemoteControl: false,
          PowerCord: false,
          TvBaseStand: false,
          BaseStandHardware: false,
        };
      }
      if (!mf[categoryKey] && categoryKey === "Motorcycle") {
        mf.Motorcycle = {
          Manufacturer: false,
          Model: false,
          Year: false,
          SerialNumberVin: false,
          Color: false,
          OdometerMiles: false,
          Km: false,
          AccessoriesIncluded: false,
          GasTankEmpty: false,
          FuelSystemPurged: false,
          BatteryDisconnected: false,
        };
      }
      if (!mf[categoryKey]) return;
      mf[categoryKey]![fieldKey] = value;
    });
  };

  const handleInactivityTimeChange = (value: number) => {
    if (isNaN(value) || value < 0) return;

    setCompanyConfig((draft) => {
      if (!draft) return;
      draft.settings.opsAppConfiguration.enforceAppLockScreen.enterInactivityTimeMilliseconds =
        value;
    });
  };

  const handleReportsSharingToggle = (
    item: ReportsSharingCatalogItem,
    checked: boolean
  ) => {
    setCompanyConfig((draft) => {
      if (!draft) return;
      const opsAppConfiguration = draft.settings.opsAppConfiguration;

      if (!opsAppConfiguration.reportsSharingConfig) {
        opsAppConfiguration.reportsSharingConfig = getDefaultReportsSharingConfig();
      }

      opsAppConfiguration.reportsSharingConfig.selectedItems = toggleReportsSharingSelectedItem(
        opsAppConfiguration.reportsSharingConfig.selectedItems,
        item,
        checked
      );
    });
  };

  // Handler for config change via ConfigSwitch
  const handleConfigChange = (path: string, value: any) => {
    setCompanyConfig((draft) => {
      if (!draft) return;

      const parts = path.split(".");
      const lastPart = parts.pop()!;
      let current: any = draft;

      for (const part of parts) {
        if (current[part] === undefined || current[part] === null) {
          current[part] = {};
        }
        current = current[part];
      }

      current[lastPart] = value;

      if (path === "settings.opsAppConfiguration.quickOrder.enabled" && value === false) {
        draft.settings.opsAppConfiguration.quickOrder.managementOrderReportEnabled = false;
      }
    });
  };

  const renderContent = () => {
    if (isFetching) {
      return (
        <div style={{ display: "flex", gap: "20px", flexDirection: "column" }}>
          {Array.from({ length: 4 }).map((_, index) => {
            return <Skeleton variant="rounded" height="40px" key={index} />;
          })}
        </div>
      );
    }

    if (!companyConfig) {
      return (
        <div>
          {translate("systemConfigurationIsNotAvailablePleaseContactTheAdministratorForAssistance")}
        </div>
      );
    }

    const appLockScreenIsEnabled =
      companyConfig.settings.opsAppConfiguration.enforceAppLockScreen.enabled;
    const appLockScreenDescription = translate(
      "ifAUserIsInactiveForASetPeriodTheAppLocksAutomaticallyForSecurityUsersChooseAPinAtFirstLoginAndCanResetItUsingTheirUsernameAndPasswordThisPreventsUnauthorizedAccessToSensitiveData"
    );
    const inactivityTimeValue =
      companyConfig.settings.opsAppConfiguration.enforceAppLockScreen
        .enterInactivityTimeMilliseconds;

    const itemCategoryEnabled =
      companyConfig.settings.opsAppConfiguration.itemCategoryConfig.enabled;
    const categoryMandatoryFieldsActive =
      companyConfig.settings.opsAppConfiguration.itemCategoryConfig.mandatoryFieldsActive;
    const categoryMandatoryFields =
      companyConfig.settings.opsAppConfiguration.itemCategoryConfig.mandatoryFields;

    const isAdmin = currentUser?.typeUser === UserTypes.ADMIN;

    const canEditOperationsApp = currentUser?.resources?.some(
      (resource) => resource.name === "Operations app" && resource.roles_resources?.edit
    );

    const canManageOperationsAppSettings = isAdmin && canEditOperationsApp;

    return (
      <Container>
        <ConfigSwitch
          key="itemCategoryConfig"
          title={translate("categorizeItemsInOperationsApp")}
          path="settings.opsAppConfiguration.itemCategoryConfig.enabled"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
          customDetails={
            <ItemCategoryConfigDetails
              isEnabled={itemCategoryEnabled}
              categories={categoryMandatoryFieldsActive}
              mandatoryFields={categoryMandatoryFields}
              onCategoryToggle={handleCategoryToggle}
              onMandatoryFieldToggle={handleMandatoryFieldToggle}
            />
          }
        />

        <ConfigSwitch
          key="startingInventoryNumber"
          title={translate("inventoryNumberingIsTheStartingInventoryNumberEditable")}
          path="settings.opsAppConfiguration.startingInventoryNumber.editable"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
        />

        <ConfigSwitch
          key="mandatoryLoadIntoStorage"
          title={translate("loadIntoStorageBoxIsMandatoryInUpliftTask")}
          description={translate(
            "ifVolumeConfirmationIsMandatoryThenStorageBoxMandatoryOptionMustBeSetToFalse"
          )}
          path="settings.opsAppConfiguration.mandatoryLoadIntoStorageBoxForUpliftTasks"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
        />

        {false && (
          <ConfigSwitch
            key="enforceAppLockScreen"
            title={translate("enforceAppLockScreen")}
            path="settings.opsAppConfiguration.enforceAppLockScreen.enabled"
            companyConfig={companyConfig}
            onConfigChange={handleConfigChange}
            customDetails={
              <OperationsAppLockScreenConfigDetails
                details={appLockScreenDescription}
                isEnabled={appLockScreenIsEnabled}
                inactivityTimeValue={inactivityTimeValue}
                onInactivityTimeChange={handleInactivityTimeChange}
              />
            }
          />
        )}

        {false && (
          <ConfigSwitch
            key="militaryPurpose"
            title={translate("usingTheAppForMilitaryPurpose")}
            path="settings.opsAppConfiguration.militaryPurposeAppUsage.enabled"
            companyConfig={companyConfig}
            onConfigChange={handleConfigChange}
          />
        )}

        <ConfigSwitch
          key="fieldRelatedSettings"
          title={translate("fieldRelatedSettings")}
          path="settings.opsAppConfiguration.fieldsRelatedSettings.enabled"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
          customDetails={
            <InventoryTable
              inventoryFieldsConfig={
                companyConfig.settings.opsAppConfiguration.fieldsRelatedSettings
                  .inventoryFieldsConfig
              }
              onChange={handleInventoryFieldChange}
            />
          }
        />

        <ConfigSwitch
          key="quickOrderEnabled"
          title={translate("enableQuickOrder")}
          path="settings.opsAppConfiguration.quickOrder.enabled"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
        />

        <ConfigSwitch
          key="managementOrderReportEnabled"
          title={translate("showOrderReportInManagement")}
          path="settings.opsAppConfiguration.quickOrder.managementOrderReportEnabled"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
          disabled={!companyConfig.settings.opsAppConfiguration.quickOrder.enabled}
        />

        <ConfigSwitch
          key="reportsSharingConfig"
          title={translate("reportsAvailableForSharingViaOpsApp")}
          hideSwitch
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
          customDetails={
            <ReportsSharingConfigDetails
              catalogItems={reportsSharingCatalog}
              disabled={!canManageOperationsAppSettings}
              isLoading={isFetchingReportGroups || isFetchingChecklists}
              selectedItems={
                companyConfig.settings.opsAppConfiguration.reportsSharingConfig.selectedItems
              }
              onToggle={handleReportsSharingToggle}
            />
          }
        />

        <ConfigSwitch
          key="propertyDamageReportSettings"
          title={translate("propertyDamageReport")}
          path="settings.opsAppConfiguration.propertyDamageReportConfig.propertyDamageReportRequired"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
          disabled={!canManageOperationsAppSettings}
          customDetails={
            currentUser?.company.isMilitary ? (
              <OperationsAppSettingsPagePropertyDamageReportConfig
                config={companyConfig.settings.opsAppConfiguration.propertyDamageReportConfig}
                onConfigChange={handleConfigChange}
                disabled={!canManageOperationsAppSettings}
              />
            ) : undefined
          }
        />

        <ConfigSwitch
          key="upliftCompletionPhotoRequired"
          title={translate("photoRequiredAtUpliftCompletion")}
          path="settings.opsAppConfiguration.propertyDamageReportConfig.upliftCompletionPhotoRequired"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
          disabled={!canManageOperationsAppSettings}
        />

        <ConfigSwitch
          key="showLastFinishedTasks"
          title={translate("showLastFinishedTasks")}
          path="settings.opsAppConfiguration.showLastFinishedTasks.enabled"
          companyConfig={companyConfig}
          onConfigChange={handleConfigChange}
          disabled={!canManageOperationsAppSettings}
        />
      </Container>
    );
  };

  return (
    <div>
      <AppActionSection
        title={translate("operationsApp")}
        buttons={[
          {
            label: translate("cancel"),
            color: Color.WARNING,
            position: "left",
            onClick() {
              if (data) setCompanyConfig(normalizeCompanyConfig(data));
            },
          },
          {
            label: translate("update"),
            variant: "contained",
            color: Color.BRAND,
            position: "right",
            disabled: !companyConfig,
            loading: isSaving || isFetching,
            onClick() {
              if (companyConfig) mutate(companyConfig);
            },
          },
        ]}
      >
        {renderContent()}
      </AppActionSection>
    </div>
  );
};
